// src/api/conversationService.js
import { EventSourcePolyfill } from 'event-source-polyfill';
import apiClient from './axiosConfig'; // Import the configured axios client with baseURL including /api/v1

/**
 * Handles API errors, formatting them into a consistent error object.
 * @param {Error} error - The error object, typically from Axios.
 * @param {string} operation - A string describing the operation that failed (e.g., "listing agents").
 * @returns {{isError: boolean, message: string, status: number|null, originalError: Error}}
 */
const handleApiError = (error, operation) => {
  console.error(`${error.config?.method?.toUpperCase()} ${error.config?.url} - Error ${operation}:`, error);
  let message = `An error occurred while ${operation}.`;
  const status = error.response?.status || null;

  if (error.response?.data) {
    const d = error.response.data;
    if (typeof d.detail === 'string') {
        message = d.detail;
    } else if (Array.isArray(d.detail)) {
        message = d.detail.map(err => {
            const loc = err.loc?.slice(1).join('.') || 'field';
            return `${loc}: ${err.msg}`;
        }).join('; ');
         if (!message) message = `Validation error during ${operation}.`;
    } else if (typeof d === 'string' && d.length < 500 && !d.trim().startsWith('<')) {
        message = d;
    } else if (d.message && typeof d.message === 'string') {
        message = d.message;
    } else if (status) {
         message = `Request failed with status code ${status} during ${operation}.`;
    }
     if (typeof message === 'string' && message.trim().startsWith('<')) {
         message = `Received an HTML error page (status ${status || 'unknown'}) during ${operation}.`;
     }
  } else if (error.request) {
    message = `No response from server during ${operation}. Please check your network connection.`;
  } else if (error.message) {
    message = error.message;
  }

  return {
    isError: true,
    message,
    status: status,
    originalError: error
  };
};


export const conversationService = {

  /**
   * Fetches a list of conversations with pagination, filtering, searching, and sorting.
   * apiClient.defaults.baseURL should be "YOUR_ROOT_APP_URL/api/v1"
   * The backend is expected to return { items: [], total: N }.
   */
  listConversations: async (
    agentId = null,
    limit = 10,
    offset = 0,
    searchQuery = null,
    sortBy = 'updated_at',
    sortOrder = 'desc'
  ) => {
    try {
      const params = new URLSearchParams();
      if (agentId) params.append('agent_id', agentId);
      if (limit !== null) params.append('limit', String(limit));
      if (offset !== null) params.append('offset', String(offset));
      if (searchQuery && searchQuery.trim() !== '') params.append('search_query', searchQuery);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);

      // Path is relative to apiClient's baseURL (e.g., "/conversations")
      const { data } = await apiClient.get(`/conversations`, { params });
      
      if (data && Array.isArray(data.items) && typeof data.total === 'number') {
        return data; // Expected paginated response
      } else {
        // If the backend doesn't return the expected paginated structure, it's an error.
        console.error(
            'conversationService.listConversations: API response format is unexpected. Expected { items: [], total: N }.', 
            data
        );
        let message = 'Received_malformed_data_from_server_when_listing_conversations.';
        if (Array.isArray(data)) {
            // This indicates the backend is still returning the old, non-paginated flat array.
            message = 'Received_deprecated_flat_array_from_server._Backend_API_needs_update_to_return_{_items:_[],_total:_N_}.';
            console.error("CRITICAL: Backend API for listConversations is returning a deprecated flat array format.");
        }
        return {
          isError: true,
          message: message,
          status: null, // Status might be available on error.response directly if it's an Axios error
        };
      }
    } catch (err) {
      return handleApiError(err, 'listing conversations');
    }
  },

  /**
   * Fetches the full details of a specific conversation by its ID.
   * apiClient.defaults.baseURL should be "YOUR_ROOT_APP_URL/api/v1"
   * @param {string} conversationId - The ID of the conversation.
   * @param {boolean} [includeInternalParts=false] - Whether to request internal message parts.
   */
  getConversationById: async (conversationId, includeInternalParts = false) => {
    if (!conversationId) {
        return { isError: true, message: 'Conversation ID is required.', status: 400 };
    }
    try {
      const params = new URLSearchParams();
      if (includeInternalParts) {
        params.append('include_internal_parts', 'true');
      }
      
      // Path is relative to apiClient's baseURL (e.g., "/conversations/some-id")
      // Pass params only if there are any
      const requestConfig = params.toString() ? { params } : {};
      const { data } = await apiClient.get(`/conversations/${conversationId}`, requestConfig);
      return data;
    } catch (err) {
      return handleApiError(err, `fetching conversation ${conversationId}`);
    }
  },

  /**
   * Initializes a secure test chat session for the logged-in user with a specific agent.
   * apiClient.defaults.baseURL should be "YOUR_ROOT_APP_URL/api/v1"
   */
  async initTestChatSession(agentId, initialContext = null) {
    if (!agentId) {
      return { isError: true, message: "Agent ID is required to initialize test chat.", status: 400 };
    }
    try {
      const payload = initialContext ? { initial_context: initialContext } : {};
      // Path is relative to apiClient's baseURL (e.g., "/agents/some-id/init-test-chat")
      const response = await apiClient.post(`/agents/${agentId}/init-test-chat`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error initializing test chat session for agent ${agentId}:`, error);
      return handleApiError(error, 'initialize test chat session');
    }
  },

  /**
   * Stream a conversation with an agent via Server-Sent Events (SSE).
   * This method requires a Widget Access Token (WAT) with the proper scope.
   * It constructs the full URL manually as EventSourcePolyfill doesn't use apiClient's baseURL.
   */
  streamConversation: (agentId, body, token, onMessage, onError, onOpen) => {
    if (!agentId || !body || !token || !onMessage || !onError) {
        const errorMsg = "streamConversation: Missing required parameters (agentId, body, token, onMessage, onError).";
        console.error(errorMsg);
        // Consider how to handle this: maybe throw an error or call onError immediately.
        // For now, allowing EventSourcePolyfill to potentially fail.
    }

    // process.env.REACT_APP_API_URL should be the root URL (e.g., http://localhost:8000)
    const rootApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'; 
    const url = `${rootApiUrl}/api/v1/agents/${agentId}/conversations/stream`;

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      // 'Accept': 'text/event-stream', // EventSourcePolyfill adds this by default
    };

    const es = new EventSourcePolyfill(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      // heartbeatTimeout: 120000, 
    });

    if (onOpen) {
      es.onopen = onOpen;
    }
    es.onmessage = onMessage;

    es.onerror = (errorEvent) => {
      console.error("SSE Connection Error:", errorEvent);
      onError(errorEvent);
    };

    return es;
  },
};